import { getPlayers } from "@/api/publicRequests/players/getPlayers";
import { FiltersRow } from "@/components/organisms/Players/FlitersRow/FiltersRow";
import LoadingPlayersList from "@/components/organisms/PlayersList/LoadingPlayersList";
import { MyEvolutionsEmptyState } from "@/components/organisms/PlayersList/MyEvolutionsEmptyState";
import { PlayersListEmptyState } from "@/components/organisms/PlayersList/PlayersListEmptyState";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME, PINNED_ASSET_SLUG } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import { Button as DsButton } from "@/design-system/Button/Button";
import { Pagination } from "@/design-system/Pagination/Pagination";
import { RoleSelectSheetContent } from "@/design-system/RoleSelectPanel/RoleSelectPanel";
import * as playersMixpanel from "@/events/playersMixpanel";
import { WingsCupBanner } from "@/features/WingsCup/WingsCup";
import {
  quickFilterKeyFromType,
  useMyClubFilter,
} from "@/hooks/useMyClubFilter";
import { cn } from "@/lib/utils";
import type { PlayersSortByType } from "@repo/shared/api";
import { useResultCount } from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";
import { buildRoleGroups } from "@repo/shared/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useParams, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { PlayerList } from "../../components/organisms/PlayersList/PlayersList";
import { PlayersToolbar } from "../../components/organisms/PlayersToolbar/PlayersToolbar";
import useDebounce from "../../hooks/useDebounce";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import {
  FAVORITE_CLUBS_AND_COUNTRIES_KEY,
  MetaRatingsPlayer,
  PlayerRole,
  activeFiltersSelector,
  clearAllFilters,
  clearFilter,
  clearPlayers,
  defaultPositions,
  fetchPlayers,
  ownedOrEvolvedSelector,
  playerFiltersAsQueryParamsSelector,
  playersSelector,
  updateFilter,
} from "../../redux/players/playersSlice";
import { AppDispatch } from "../../redux/store";
import { BREAKPOINTS } from "../../settings";
import { PlayerFiltersPanel } from "./FiltersPanel/PlayerFiltersPanel";
import { PlayersViewToggle, usePlayersView } from "./PlayersViewToggle";
import { QuickFilters } from "./QuickFilters";
import { StickySidebar } from "./StickySidebar";

export type QueryParams = { key: string; values: (number | string)[] };

type DropdownType = "playerRole" | "filters" | "none";

export const PlayersPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation(["players", "common"]);

  const {
    players,
    totalPages,
    playersStatus,
    playerFilters,
    requestedFromPage,
  } = useSelector(playersSelector);
  const [searchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState<PlayersSortByType>(
    (searchParams.get("sort") as PlayersSortByType) || "date-added",
  );
  const [didLoadSortBy, setDidLoadSortBy] =
    useState<PlayersSortByType>("meta-rating");
  const { resourceId } = useParams();

  const { playerRoles } = useSelector(playersSelector);
  const ownedOrEvolvedFilters = useSelector(ownedOrEvolvedSelector);
  const stableQueryParams = useSelector(playerFiltersAsQueryParamsSelector);
  const hasFilters = useMemo(
    () => stableQueryParams.length > 0,
    [stableQueryParams],
  );
  const [page, setPage] = useState(1);
  const [playerRole, setPlayerRole] = useState<PlayerRole>(undefined);
  const [dropdown, setDropdown] = useState<DropdownType>("none");
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = usePlayersView();

  const { debouncedSearchTerm, errorMessage: searchTermErrorMessage } =
    useDebounce(searchTerm, {
      minLength: 3,
      contextForErrorMessage: t("common:playerName"),
    });

  const [player, setPlayer] = useState<
    MetaRatingsPlayer & { show3Chem: boolean }
  >();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const refPlayerRole = useOutsideClick(
    () => dropdown === "playerRole" && setDropdown("none"),
  );

  const refFilters = useOutsideClick(
    () => dropdown === "filters" && setDropdown("none"),
  );

  const handleToolbarAction = (inputDropdown: DropdownType) => {
    setDropdown(dropdown === inputDropdown ? "none" : inputDropdown);
  };

  const [playerRoleInitialized, setPlayerRoleInitialized] = useState(false);

  useEffect(() => {
    if (playerRoles.length === 0) return;
    const urlPlayerRoleId = searchParams.get("playerRoleId");
    if (urlPlayerRoleId) {
      const role = playerRoles.find((r) => r.id === parseInt(urlPlayerRoleId));
      if (role) {
        setPlayerRole(role);
        // The backend can't sort a role by trending
        if (sortBy === "trending") setSortBy("meta-rating");
      }
    }
    setPlayerRoleInitialized(true);
  }, [playerRoles]);

  useUrlFilters({
    playerFilters,
    sort: sortBy,
    ownedOrEvolvedFilters,
    stableQueryParams,
    playerRole: playerRole ?? "best",
    readyToSync: playerRoleInitialized && !resourceId,
  });

  useEffect(() => {
    if (requestedFromPage !== "players") {
      dispatch(clearPlayers());
    }
    return () => {
      dispatch(clearAllFilters());
    };
  }, [dispatch]);

  // A new search or filter set starts from the first page.
  useEffect(() => {
    setPage(1);
  }, [stableQueryParams, sortBy, debouncedSearchTerm]);

  // Figure out what this does exactly
  useEffect(() => {
    const player = players.find(
      (p) =>
        p.eaPlayerId === +resourceId ||
        (p.resourceId === +resourceId && !p.eaPlayerId),
    );
    setPlayer({
      ...player,
      show3Chem: didLoadSortBy === "meta-rating-3-chem",
    });
  }, [players, resourceId, playerRole]);

  // "2 active · 1,420+ players" under the filters title, as on mobile.
  const activeFilterCount = useSelector(activeFiltersSelector).length;
  const resultCount = useResultCount({
    key: JSON.stringify(stableQueryParams),
    enabled: dropdown === "filters",
    fetchFirstPage: () =>
      getPlayers({
        page: 1,
        sort: "meta-rating",
        myClub: ownedOrEvolvedFilters?.category === "Owned",
        myEvolvedPlayers: ownedOrEvolvedFilters?.category === "Evolved",
        activeSquad: ownedOrEvolvedFilters?.category === "ActiveSquad",
        queryParams: stableQueryParams.filter(
          (qp) => !(FAVORITE_CLUBS_AND_COUNTRIES_KEY in qp),
        ),
        favoriteClubsAndCountries: stableQueryParams.some(
          (qp) => FAVORITE_CLUBS_AND_COUNTRIES_KEY in qp,
        ),
      }),
  });
  const filtersSubtitle =
    [
      activeFilterCount > 0
        ? t("filters.panel.activeCount", { count: activeFilterCount })
        : undefined,
      resultCount
        ? t("filters.panel.playerCount", {
            count: resultCount.count,
            value: `${resultCount.count.toLocaleString()}${resultCount.approximate ? "+" : ""}`,
          })
        : undefined,
    ]
      .filter(Boolean)
      .join(" · ") || undefined;

  const roleGroups = useMemo(
    () =>
      buildRoleGroups({
        playerRoles,
        positionsOrder: defaultPositions.map(({ label }) => label),
      }),
    [playerRoles],
  );

  const handleSelectPlayerRole = (pr: PlayerRole) => {
    setPage(1);
    setPlayerRole(pr);
    if (sortBy === "date-added" || (pr && sortBy === "trending")) {
      setSortBy("meta-rating");
    }
  };
  useEffect(() => {
    dispatch(
      fetchPlayers({
        page,
        sort: sortBy,
        myClub: ownedOrEvolvedFilters?.category === "Owned",
        myEvolvedPlayers: ownedOrEvolvedFilters?.category === "Evolved",
        activeSquad: ownedOrEvolvedFilters?.category === "ActiveSquad",
        playerRoleId: playerRole?.id,
        queryParams: stableQueryParams,
        evolutions: false,
        searchText: debouncedSearchTerm,
        requestedFromPage: "players",
      }),
    ).then(() => {
      if (didLoadSortBy !== sortBy) {
        setDidLoadSortBy(sortBy);
      }
    });
  }, [
    dispatch,
    page,
    sortBy,
    playerRole,
    stableQueryParams,
    ownedOrEvolvedFilters,
    debouncedSearchTerm,
  ]);

  const handleSearch = (searchTerm: string) => {
    if (sortBy === "date-added" && searchTerm.length > 2) {
      setPage(1);
      setSortBy("meta-rating");
    }
    setSearchTerm(searchTerm);
  };

  const handleMyClubClicked = useMyClubFilter(
    (f) => {
      dispatch(updateFilter(f));
      if (sortBy === "date-added") {
        setSortBy("meta-rating");
      }
    },
    {
      onFilterApplied: (type) =>
        playersMixpanel.trackQuickFilter({
          subsection: "Overview",
          filters: quickFilterKeyFromType(type),
        }),
      origin: { section: "Players", subsection: "Overview" },
      upgradeTracking: {
        section: "Players",
        subsection: "Overview",
        context: "filter",
      },
    },
  );

  // Tracks the filters once per panel session, when it closes.
  const filtersTouched = useRef(false);
  useEffect(() => {
    if (dropdown !== "filters" && filtersTouched.current) {
      filtersTouched.current = false;
      onFiltersUpdated();
    }
  }, [dropdown]);

  const onFiltersUpdated = () => {
    const filters = stableQueryParams
      .map((qp) => Object.keys(qp ?? {})[0])
      .filter(Boolean);
    playersMixpanel.trackFilter({
      subsection: "Overview",
      filters,
    });
  };

  if (resourceId) {
    return <Outlet context={player} />;
  }

  return (
    <main className="flex flex-col w-full bg-background">
      <HeroSection
        helmetTitle={t("page.title", { game: GAME_NAME })}
        helmetDescription={t("page.description", { game: GAME_NAME })}
        helmetCanonicalLink={canonicalUrl("/players")}
        shouldBeInTheDocumentFlow
        // backgroundImageUrl={
        //   import.meta.env.VITE_APP_ASSETS_BASE_URL +
        //   `/${PINNED_ASSET_SLUG}/page-headers/player-page.png`
        // }
        // backgroundImageLayer="linear-gradient(180deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 1) 100%)"
      />
      <PlayersToolbar
        searchTerm={searchTerm}
        setSearchTerm={handleSearch}
        handleToolbarAction={handleToolbarAction}
        isMobile={isMobile}
        hasFilters={hasFilters}
        sortBy={sortBy}
        setSortBy={(v) => {
          // Last pick wins: trending drops the role
          if (v === "trending") setPlayerRole(undefined);
          setSortBy(v);
        }}
        playerRole={playerRole}
        pageType="players"
        viewToggle={<PlayersViewToggle value={view} onChange={setView} />}
        quickFilters={
          <QuickFilters
            onMyClubClicked={handleMyClubClicked}
            onFiltersChanged={() => {
              if (sortBy === "date-added") setSortBy("meta-rating");
            }}
          />
        }
      />
      <div
        className={cn(
          "flex flex-col gap-y-1 desktop:gap-y-4 mx-auto items-center w-full max-w-content px-3 large:px-16 touch-device:px-4 min-h-screen",
        )}
      >
        <div className="w-full flex flex-row gap-x-1">
          <div
            className={cn(
              "w-full flex flex-col gap-y-2 mt-3",
              (dropdown === "playerRole" || dropdown === "filters") && "",
            )}
          >
            {(playersStatus === "pending" || playersStatus === "idle") &&
            (players.length === 0 || isTouchDevice) ? (
              <LoadingPlayersList />
            ) : players.length === 0 || searchTermErrorMessage ? (
              <PlayersListEmptyState
                searchTermErrorMessage={searchTermErrorMessage}
                category={ownedOrEvolvedFilters?.category}
                clubContext="players"
                section="Players"
                evolutionsEmptyState={
                  <MyEvolutionsEmptyState section="Players" />
                }
              />
            ) : (
              <PlayerList
                players={players}
                show3Chem={didLoadSortBy === "meta-rating-3-chem"}
                playerRoleId={playerRole?.id}
                view={view}
                afterSecondRow={
                  <WingsCupBanner unit="players" className="col-span-full" />
                }
              />
            )}
          </div>
          {(dropdown === "playerRole" || dropdown === "filters") && (
            <StickySidebar
              handleClose={() => handleToolbarAction("none")}
              title={
                dropdown === "playerRole" ? undefined : t("common:filters")
              }
              subtitle={filtersSubtitle}
              action={
                <DsButton
                  variant="secondary"
                  size="small"
                  onClick={() => handleToolbarAction("none")}
                >
                  {t("common:apply")}
                </DsButton>
              }
            >
              {dropdown === "playerRole" ? (
                <div ref={refPlayerRole} className="pt-4">
                  <RoleSelectSheetContent
                    groups={roleGroups}
                    selectedRoleId={playerRole?.id}
                    subtitle={t("roleSelect.subtitle")}
                    onPick={(role) => {
                      handleSelectPlayerRole(
                        playerRoles.find((r) => r.id === role?.id),
                      );
                      handleToolbarAction("none");
                    }}
                    onClose={() => handleToolbarAction("none")}
                  />
                </div>
              ) : (
                <div ref={refFilters}>
                  <PlayerFiltersPanel
                    filters={playerFilters}
                    activeCount={stableQueryParams.length}
                    onUpdateFilter={(f) => {
                      filtersTouched.current = true;
                      if (window.scrollY > 220) {
                        window.scrollTo(0, 220);
                      }
                      dispatch(updateFilter(f));
                      if (sortBy === "date-added") {
                        setSortBy("meta-rating");
                      }
                    }}
                    onClearFilter={(key) => dispatch(clearFilter(key))}
                    onClearAll={() => dispatch(clearAllFilters())}
                    summary={filtersSubtitle ?? t("common:noFiltersApplied")}
                    chips={
                      isMobile && (
                        <div className="border-b border-edge-strong px-4 py-2">
                          {hasFilters ? (
                            <FiltersRow
                              resetLabel={`${t("filters.panel.reset")} ${activeFilterCount}`}
                            />
                          ) : (
                            <span className="text-xs text-foreground-muted">
                              {t("filters.panel.noFiltersYet")}
                            </span>
                          )}
                        </div>
                      )
                    }
                  />
                </div>
              )}
            </StickySidebar>
          )}
        </div>
        {players.length > 0 && !searchTermErrorMessage && (
          <Pagination
            className="mt-6 mb-10 w-full items-end"
            currentPage={page}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              window.scrollTo(0, 0);
            }}
          />
        )}
      </div>
    </main>
  );
};

export default PlayersPage;
